import fetchLoyaltyActionRewards from 'actions/loyalty/fetchLoyaltyActionRewards'
import fetchLoyaltyAccount from 'actions/loyalty/fetchLoyaltyAccount'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useTabVisibility from 'hooks/useTabVisibility'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import {
  getAdvancedLoyaltyActionRewardNames,
  getLoyaltyActionRewardAppliedCountSummary,
  readLoyaltyActionRewardCelebrationSummary,
  seedLoyaltyActionRewardCelebrationSummaryIfAbsent,
  writeLoyaltyActionRewardCelebrationSummary,
} from 'loyalty/storage/loyaltyActionRewardCelebration'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useStore } from 'react-redux'

export interface UseLoyaltyActionRewardReturnCelebrationResult {
  celebrationActive: boolean
  celebrationId: number
}

/** Compare last-seen quest applies (localStorage) to a fresh catalogue fetch and celebrate newly applied ones. */
function useLoyaltyActionRewardReturnCelebration(): UseLoyaltyActionRewardReturnCelebrationResult {
  const dispatch = useAppDispatch()
  const store = useStore<App.State>()
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const isTabVisible = useTabVisibility()

  const isLuxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const subscriptionOffers = useAppSelector(state => state.luxPlus.products.subscription.offers)
  const subscriptionError = useAppSelector(state => state.luxPlus.products.subscription.error)
  // Hidden dependency: a force refetch still reads LuxPlusRenewal offerId from
  // the catalog in Redux (getLoyaltyActionRewardEarnRequest). Do not refresh
  // until that catalog is present, or batch calculate omits offerId.
  const subscriptionCatalogReady = !isLuxPlusEnabled || !!subscriptionOffers || !!subscriptionError

  const actionRewardsData = useAppSelector(state => state.loyalty.actionRewards.data)
  const [celebratingActionNames, setCelebratingActionNames] = useState<Array<string>>([])
  const [celebrationId, setCelebrationId] = useState(0)
  const refreshInFlightRef = useRef(false) // ignore overlapping refresh() calls while we wait on Redux
  const mountedRef = useRef(true) // skip setState if the store subscriber fires after unmount

  const refresh = useCallback(() => {
    if (!memberId || refreshInFlightRef.current) {
      return
    }

    // A sibling fetch (typically useLoyaltyActionRewards on the same mount) may
    // already be in flight. Still subscribe so we can celebrate when it
    // completes, but do not start a second force refetch.
    const alreadyFetching = store.getState().loyalty.actionRewards.fetching

    refreshInFlightRef.current = true
    const unsubscribe = store.subscribe(() => {
      const { fetching, data, error } = store.getState().loyalty.actionRewards
      if (fetching) {
        return // wait until the current fetch settles
      }

      unsubscribe()
      refreshInFlightRef.current = false

      if (error || !data) {
        return
      }

      const currentSummary = getLoyaltyActionRewardAppliedCountSummary(data)
      const previousSummary = readLoyaltyActionRewardCelebrationSummary(memberId)
      writeLoyaltyActionRewardCelebrationSummary(memberId, currentSummary) // persist as baseline for next visit

      // Prevent updating post unmount and prevent incomparable summaries
      if (previousSummary && mountedRef.current) {
        const actionNames = getAdvancedLoyaltyActionRewardNames(previousSummary, currentSummary)
        setCelebratingActionNames(actionNames)
        if (actionNames.length > 0) {
          setCelebrationId(id => id + 1)
          dispatch(fetchLoyaltyAccount(undefined, { force: true }))
        }
      }
    })

    if (!alreadyFetching) {
      dispatch(fetchLoyaltyActionRewards({ force: true }))
    }
  }, [dispatch, memberId, store])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Seed baseline first
  useEffect(() => {
    if (memberId && isTabVisible && actionRewardsData) {
      seedLoyaltyActionRewardCelebrationSummaryIfAbsent(memberId, actionRewardsData)
    }
  }, [actionRewardsData, isTabVisible, memberId])

  // Refresh on tab visibility and only when subscription catalog is ready
  useEffect(() => {
    if (isTabVisible && subscriptionCatalogReady) {
      refresh()
    }
  }, [isTabVisible, refresh, subscriptionCatalogReady])

  return {
    celebrationActive: celebratingActionNames.length > 0,
    celebrationId,
  }
}

export default useLoyaltyActionRewardReturnCelebration
